import { DateFormats, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { DollarAmount } from '@strata/react-dollar-amount';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';
import { ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppRoutes } from 'services/routes';
import { ClaimSummary, useGetClaimsQuery } from 'store/api/medbillingApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<ClaimSummary>
>['sortProperties'] = [{ id: 'submitted_date', desc: true }];

const useFilterCategories = (
    filterValues?: Record<string, string[]>,
): FilterCategory<ClaimSummary>[] => {
    const filters: { key: keyof ClaimSummary; title: string }[] = [
        { key: 'product_service_display_name', title: 'Product' },
        { key: 'claim_status', title: 'Claim Status' },
    ];
    return filters.map((filter) => ({
        ...filter,
        customValues: (filterValues?.[filter.key] ?? []).map((value) => ({
            value,
            text: (() => {
                if (filter.key === 'claim_status') {
                    return ClaimStatusIndicator.StatusMessages[
                        value as (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses]
                    ];
                }
                return value;
            })(),
        })),
    }));
};

const columns: ColumnDef<ClaimSummary>[] = [
    {
        accessorKey: 'last_name',
        header: 'Patient',
        size: 160,
        cell: ({ getValue, row }) => (
            <PrimaryData data={formatNameLastFirst(getValue(), row.original.first_name)} />
        ),
    },
    {
        accessorKey: 'start_date_of_service',
        header: 'Date Of Service Range',
        size: 140,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={`${formatDate(getValue())} - ${formatDate(row.original.end_date_of_service)}`}
            />
        ),
    },
    {
        accessorKey: 'submitted_date',
        header: 'Submitted Date/Time',
        size: 130,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'product_service_display_name',
        header: 'Product',
        size: 100,
    },
    {
        accessorKey: 'submitted_amount',
        header: 'Submitted Amt.',
        size: 100,
        cell: ({ getValue }) => <DollarAmount amount={getValue()} showZeroValue />,
    },
    {
        accessorKey: 'paid_amount',
        header: 'Paid Amt.',
        size: 100,
        cell: ({ getValue }) => <DollarAmount amount={getValue()} showZeroValue />,
    },
    {
        accessorKey: 'claim_status',
        header: 'Claim Status',
        size: 120,
        enableSorting: false,
        cell: ({ getValue }) => <ClaimStatusIndicator status={getValue()} />,
    },
];

export const ClaimsListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.CLAIMS, initialSortProperties);

    const apiQueryParams = {
        page,
        searchText: search,
        activeFilters,
        sort,
    };
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetClaimsQuery({
        ...apiQueryParams,
        page: 0,
    });
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetClaimsQuery(apiQueryParams);

    const filterCategories = useFilterCategories(firstPageData?.filters);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Claims History"
                secondaryTitle={loggedInSupplierName}
                data={currentPageData?.claims}
                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                columns={columns}
                manualSorting
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                onPageChange={updateCurrentPage}
                totalCount={firstPageData?.count}
                onRowClick={(rowData) => navigate(AppRoutes.claimDetails(rowData.transaction_id))}
            />
        </FormPageContainer>
    );
};
